import type {
  ArtifactVisibility,
  WorkspaceArtifact,
} from '@/lib/api/workspace-artifacts';

/**
 * How wide an artifact is shared, as the UI names it. The service carries four visibilities:
 * `restricted` (its creator alone), `conversation` (whoever can see the chat the file
 * was born in), `group` (the people its author picked) and `workspace` (every member).
 *
 * `visibility` is an OPEN union on the wire — a newer service may send a value this build has never heard
 * of — so anything unrecognized reads as `private`, the narrowest state. Showing "Shared" for a value we
 * cannot interpret would tell the user their file is wider open than we know it to be.
 */
export type ArtifactSharingState = 'private' | 'chat' | 'group' | 'workspace';

/** The sharing state of one artifact, resolved fail-closed. */
export function artifactSharingState(
  artifact: Pick<WorkspaceArtifact, 'visibility'>,
): ArtifactSharingState {
  if (artifact.visibility === 'workspace') return 'workspace';
  if (artifact.visibility === 'conversation') return 'chat';
  if (artifact.visibility === 'group') return 'group';
  return 'private';
}

/** The badge text for each state. Short enough for a table cell. */
export const ARTIFACT_SHARING_LABEL: Record<ArtifactSharingState, string> = {
  private: 'Private',
  chat: 'In chat',
  group: 'Group',
  workspace: 'Shared',
};

/** What each state means, for a tooltip — the badge alone does not say who "in chat" is. */
export const ARTIFACT_SHARING_TITLE: Record<ArtifactSharingState, string> = {
  private: 'Only you',
  chat: 'Everyone in the chat this file came from',
  group: 'You and the people you picked',
  workspace: 'Everyone in the workspace',
};

/** Which icon each state draws. The icons themselves live with the markup. */
export const STATE_ICON: Record<
  ArtifactSharingState,
  'lock' | 'chat' | 'users' | 'globe'
> = {
  private: 'lock',
  chat: 'chat',
  group: 'users',
  workspace: 'globe',
};

/** One level the share dialog offers, in the order it lists them. */
export interface ArtifactSharingLevel {
  state: ArtifactSharingState;
  visibility: ArtifactVisibility;
  label: string;
  hint: string;
  icon: 'lock' | 'chat' | 'users' | 'globe';
}

/**
 * The levels a file can be moved between, widest last — the conversation's own three plus the inherited
 * one. `chat` is offered only for a file that was actually born in a chat: `visibility: 'conversation'`
 * names THAT chat's audience, and the service answers 422 for a file with none, so rendering it would be
 * an option that cannot succeed.
 *
 * The labels are deliberately the same words the conversation dialog uses for the same reach, because
 * the two models are now the same model and a user who learned one should not have to learn the other.
 */
export function artifactSharingLevels(
  artifact: Pick<WorkspaceArtifact, 'conversationId'>,
): ArtifactSharingLevel[] {
  return [
    {
      state: 'private',
      visibility: 'restricted',
      label: 'Private',
      hint: 'Only you',
      icon: 'lock',
    },
    ...(artifact.conversationId !== null
      ? [
          {
            state: 'chat' as const,
            visibility: 'conversation' as const,
            label: 'In chat',
            hint: 'Everyone in the chat this file came from',
            icon: 'chat' as const,
          },
        ]
      : []),
    {
      state: 'group',
      visibility: 'group',
      label: 'Group',
      hint: 'You and the people you pick',
      icon: 'users',
    },
    {
      state: 'workspace',
      visibility: 'workspace',
      label: 'Workspace',
      hint: 'Everyone in the workspace',
      icon: 'globe',
    },
  ];
}

/**
 * Who can open the file, in a sentence the interface can put in front of someone — `null` when the
 * answer is "everyone in the workspace" and there is nothing to warn about.
 *
 * It exists because the interface used to answer this question with one boolean, `visibility ===
 * 'workspace'`, and call everything else private. That was true while there were two levels. With four
 * it tells people their file is narrower than it is: a file shared with a chat or with a picked list
 * reads as private, and the warning built on it went further and claimed the recipient could not open
 * the file — which is false for exactly the people it was shared with.
 *
 * The wording deliberately stops at naming the audience. Whether one particular recipient is inside it
 * is a question only the server can answer (that is what the access list is for), so the interface says
 * what is true of the FILE and does not guess about the person.
 */
export function artifactAudienceSentence(
  artifact: Pick<WorkspaceArtifact, 'visibility'>,
): string | null {
  switch (artifactSharingState(artifact)) {
    case 'workspace':
      return null;
    case 'chat':
      return 'Only the people in the chat this file came from can open it.';
    case 'group':
      return 'Only the people this file is shared with can open it.';
    default:
      return 'Only you can open this file.';
  }
}

/**
 * Which level the share dialog opens on — the file's own, but only when the dialog actually offers it.
 *
 * `visibility` is an open union, so a newer service can send a value this build has never heard of; and
 * `conversation` on a file with no `conversationId` is offered by nobody, because it names an audience
 * that does not exist. Either way the radio would render with nothing selected and Save would PATCH a
 * value the service refuses. Fail-closed to the narrowest level instead: a dialog that opens on Private
 * is honest about what this build understands, and the user can still widen it.
 */
export function initialShareVisibility(
  artifact: Pick<WorkspaceArtifact, 'visibility' | 'conversationId'>,
): ArtifactVisibility {
  const offered = artifactSharingLevels(artifact).map(
    (level) => level.visibility,
  );
  return (
    offered.find((visibility) => visibility === artifact.visibility) ??
    'restricted'
  );
}

/** Why one person can read the file, in the words the access list shows. An OPEN union from the wire. */
export const ACCESS_REASON_LABEL: Record<string, string> = {
  author: 'Created it',
  picked: 'You picked them',
  chat: 'In the chat',
  /** Sent only by an older service; the current one no longer lets owners outside the audience in. */
  workspace_owner: 'Workspace owner',
  workspace: 'Workspace member',
};

/** The reason as text, falling back to the raw value so a newer server never renders a blank cell. */
export function accessReasonLabel(reason: string): string {
  return ACCESS_REASON_LABEL[reason] ?? reason;
}
